import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnChanges,
  SimpleChanges,
  computed,
  inject,
  Input,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Language, Post } from 'src/app/core/models/core.models';
import {
  injectTranslatePostMutation,
  injectUpdatePostTranslationMutation,
} from 'src/app/api/queries/post.queries';

import { FormGroup } from '@angular/forms';
import { FormProperty } from 'src/app/core/models/form.models';
import { FormsService } from 'src/app/core/services/forms.service';
import { NomLanguages } from 'src/app/core/constants';
import { Observable, tap } from 'rxjs';
import { PostDetailsService } from 'src/app/core/services/post-details.service';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-post-translation',
  templateUrl: './post-translation.component.html',
  styleUrls: ['./post-translation.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PostTranslationComponent implements OnChanges {
  @Input() postId: string;

  private destroyRef = inject(DestroyRef);
  private readonly postDetails = inject(PostDetailsService);

  /** The post this tab is showing - the pane pushes it through `ngOnChanges`. */
  private readonly shownPostId = signal<string | null>(null);

  // `toObservable` needs an injection context, so this is a field; `ngOnInit` only
  // subscribes.
  post$: Observable<Post> = toObservable(
    computed(() => this.postDetails.postFor(this.shownPostId())),
  );

  private readonly updateTranslationMutation =
    injectUpdatePostTranslationMutation();
  private readonly translatePostMutation = injectTranslatePostMutation();

  postTranslationLoading$: Observable<boolean> = toObservable(
    computed(() => {
      const variables = this.updateTranslationMutation.variables();

      return (
        this.updateTranslationMutation.isPending() &&
        variables?.postId === this.shownPostId()
      );
    }),
  );

  post: Post;
  currentLanguage: Language = {
    name: 'English',
    id: NomLanguages.ENGLISH,
    code: 'en',
    rtl: false,
  };
  hasTranslationLanguage: boolean = false;
  hasTranslationLangauge: boolean = false;

  formGroup: FormGroup;
  formProperties: FormProperty[] = [
    {
      propertyName: 'language_picker',
      controlType: 'language-picker',
      additionalConfig: {
        hasDefault: false,
      },
    },
    {
      propertyName: 'title',
      propertyNameLanguages: {
        [NomLanguages.BULGARIAN]:
          'translation.' + NomLanguages.BULGARIAN + '.title',
        [NomLanguages.ENGLISH]:
          'translation.' + NomLanguages.ENGLISH + '.title',
        [NomLanguages.ARABIC]: 'translation.' + NomLanguages.ARABIC + '.title',
      },
      propertyLabel: this.translateService.instant(
        'post-translation.label.translated-title',
      ),
      controlType: 'text',
      additionalConfig: {
        placeholder: this.translateService.instant(
          'post-translation.placeholder.translated-title',
        ),
        disabled: true,
      },
    },
    {
      propertyName: 'translation',
      propertyLabel: this.translateService.instant(
        'post-translation.label.translated-content',
      ),
      controlType: 'textarea',
      propertyNameLanguages: {
        [NomLanguages.BULGARIAN]:
          'translation.' + NomLanguages.BULGARIAN + '.content',
        [NomLanguages.ENGLISH]:
          'translation.' + NomLanguages.ENGLISH + '.content',
        [NomLanguages.ARABIC]:
          'translation.' + NomLanguages.ARABIC + '.content',
      },
      additionalConfig: {
        placeholder: this.translateService.instant(
          'post-translation.placeholder.translated-content',
        ),
        rows: 20,
      },
    },
  ];

  constructor(
    private formService: FormsService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
  ) {}

  ngOnInit() {
    this.shownPostId.set(this.postId);
    this.formGroup = this.formService.createFormGroup(this.formProperties);
    this.post$
      .pipe(
        tap((post) => {
          this.post = post;
          this.formService.populateFormValues(this.formGroup, post);
          this.estimateHasTranslationLanguage();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  /**
   * The pane keeps one instance of this tab and re-pushes `postId` when the selected
   * post changes, so the panel has to follow it rather than bind once.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['postId']?.currentValue) {
      this.shownPostId.set(this.postId);
    }
  }

  estimateHasTranslationLanguage() {
    // The panel follows the pane's post, so this is called once with no post
    // while the next one loads.
    if (!this.post) {
      this.hasTranslationLanguage = false;
      this.cdr.detectChanges();
      return;
    }

    this.hasTranslationLanguage =
      !!this.post.translation &&
      this.currentLanguage.id in this.post.translation &&
      this.isValidObject(this.post.translation[this.currentLanguage.id]);
    this.cdr.detectChanges();
  }

  estimateTitleProperty(type: string) {
    if (type === 'social') {
      const titleProp = this.formProperties.findIndex(
        (prop) => prop.propertyName === 'title',
      );
      this.formProperties.splice(titleProp, 1);
    }
  }

  isValidObject(obj: any): boolean {
    return obj && Object.keys(obj).length > 0;
  }

  setTranslation() {
    const currentLanguageId = this.currentLanguage.id;
    const title = this.formGroup.get([
      'translation.' + currentLanguageId + '.title',
    ]).value;
    const synopsis = this.formGroup.get([
      'translation.' + currentLanguageId + '.content',
    ]).value;

    // console.log(title);

    this.updateTranslationMutation.mutate({
      postId: this.post.id,
      title: title ?? '',
      content: synopsis,
      languageId: currentLanguageId,
    });
  }

  copyFromOriginal() {
    this.formGroup.patchValue({
      ['translation.' + this.currentLanguage.id + '.title']: this.post.title,
      ['translation.' + this.currentLanguage.id + '.content']:
        this.post.content,
    });
  }

  onLanguageChanged(language: Language) {
    this.currentLanguage = language;
    this.estimateHasTranslationLanguage();
  }

  translateContent() {
    // Call the action

    const currentLanguageId = this.currentLanguage.id;
    const title = this.formGroup.get([
      'translation.' + currentLanguageId + '.title',
    ]).value;
    const content = this.formGroup.get([
      'translation.' + currentLanguageId + '.content',
    ]).value;

    this.translatePostMutation.mutate({
      postId: this.postId,
      languageId: currentLanguageId,
      title: title ?? '',
      content: content,
    });
  }
}
